/**
 * AuthorizationForm Component
 * Form for creating and editing prior authorizations
 * Works with useAuthorizationForm hook
 */

import React from 'react';
import { X, ChevronDown, AlertCircle } from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import PrimaryButtonLight from '../../common/PrimaryButtonLight';
import { UseAuthorizationFormResult } from './hooks/useAuthorizationForm';
import { disciplineCodes, disciplineLabel } from '../../../constants/disciplines';

interface AuthorizationFormProps {
  form: UseAuthorizationFormResult;
  isReadOnlyView?: boolean;
  canUpdateAuthorization?: boolean;
  canDeleteAuthorization?: boolean;
}

export const AuthorizationForm: React.FC<AuthorizationFormProps> = ({
  form,
  isReadOnlyView = false,
  canUpdateAuthorization = true,
  canDeleteAuthorization = false
}) => {
  const { formData, handleInputChange, handleSubmit, cancelForm, errors, saving } = form;
  const isEditMode = form.isEditing && !form.isViewing;

  return (
    <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold text-slate-800">
          {isEditMode ? 'Edit' : 'New'} Prior Authorization
        </h3>
        <button
          onClick={cancelForm}
          className="text-slate-400 hover:text-slate-600"
          type="button"
        >
          <X size={20} />
        </button>
      </div>

      <div>
        {/* Read-only mode notification */}
        {isReadOnlyView && (
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm text-gray-700">
              You do not have permission to edit Prior Authorizations. Viewing in read-only mode.
            </p>
          </div>
        )}

        {/* Validation Errors */}
        {errors.general && errors.general.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded-r-md">
            <div className="flex items-center mb-2">
              <AlertCircle size={16} className="text-red-600 mr-2" />
              <span className="text-sm font-medium text-red-800">Please correct the following:</span>
            </div>
            <ul className="list-disc pl-5 text-sm text-red-700">
              {errors.general.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Authorization Info */}
          <div className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-2 text-left">Basic Information</h4>
              <p className="text-sm text-gray-600 mb-4 text-left">Enter the basic authorization details</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Select the type of service being authorized</p>
                  <div className="relative">
                    <select
                      name="discipline"
                      value={formData.discipline || ''}
                      onChange={handleInputChange}
                      disabled={isReadOnlyView}
                      className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    >
                      <option value="">Select service type</option>
                      {disciplineCodes().map(code => (
                        <option key={code} value={code}>{code === 'RN' ? 'Skilled Nursing' : disciplineLabel(code)}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <ChevronDown size={16} className="text-gray-500" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Authorization Number <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Enter the authorization number from insurance</p>
                  <input
                    type="text"
                    name="authNumber"
                    value={formData.authNumber || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    placeholder="Enter authorization number"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">When the authorization becomes effective</p>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">When the authorization expires</p>
                  <input
                    type="date"
                    name="expirationDate"
                    value={formData.expirationDate || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Authorized Visits <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Number of visits authorized</p>
                  <input
                    type="number"
                    name="authorizedVisits"
                    value={formData.authorizedVisits || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    placeholder="e.g., 30"
                    min="0"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Time Per Unit
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Duration of each visit (e.g., 15 minutes, 30 minutes)</p>
                  <input
                    type="text"
                    name="timePerUnit"
                    value={formData.timePerUnit || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    placeholder="e.g., 15 minutes"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    HCPCS Code
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Healthcare Common Procedure Coding System code</p>
                  <input
                    type="text"
                    name="hcpcsCode"
                    value={formData.hcpcsCode || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    placeholder="e.g., G0156"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Current status of the authorization</p>
                  <div className="relative">
                    <select
                      name="status"
                      value={formData.status || 'PENDING'}
                      onChange={handleInputChange}
                      disabled={isReadOnlyView}
                      className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    >
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="DENIED">Denied</option>
                      <option value="EXPIRED">Expired</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <ChevronDown size={16} className="text-gray-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-2 text-left">Additional Details</h4>
              <p className="text-sm text-gray-600 mb-4 text-left">Optional information about the authorization</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Payer
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Insurance company or payer name</p>
                  <input
                    type="text"
                    name="payorName"
                    value={formData.payorName || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    placeholder="e.g., Medicare, Blue Cross"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Insurance Provider
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Specific insurance provider</p>
                  <input
                    type="text"
                    name="insuranceProvider"
                    value={formData.insuranceProvider || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    placeholder="Enter insurance provider"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Notes
                  </label>
                  <p className="text-xs text-gray-500 mb-2 text-left">Additional notes or comments</p>
                  <textarea
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleInputChange}
                    disabled={isReadOnlyView}
                    rows={3}
                    placeholder="Enter any additional notes"
                    className={`w-full border border-slate-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors resize-none ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
            <PrimaryButtonLight
              type="button"
              onClick={cancelForm}
              disabled={saving}
              className="px-4 py-2"
            >
              Cancel
            </PrimaryButtonLight>
            {!isReadOnlyView && (
              <PrimaryButton
                type="submit"
                disabled={saving}
                className="px-6 py-2"
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Authorization' : 'Create Authorization'}
              </PrimaryButton>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthorizationForm;
